import {
  type BoardCell,
  type ChipColor,
  KINGS_CROWN_MAX_HAND_SIZE,
  type KingsCrownViewerHandSlot,
  type KingsCrownViewerState,
  type KingsCrownViewerTableSlot,
  type NumberChip,
} from "@cloudnest/kings-crown-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import {
  kingsCrownMultiplayerActions,
  useKingsCrownMultiplayerStore,
} from "./multiplayer-game-store";
import "./styles/kings-crown-tokens.css";

type PickSelection =
  | { readonly source: "bag"; readonly slotId?: undefined }
  | { readonly source: "table"; readonly slotId: string };

type HandSortMode = "fixed" | "sorted";

interface RenderedHandSlot {
  readonly key: string;
  readonly slotIndex: number;
  readonly row: "fixed" | ChipColor;
  readonly chip: NumberChip | null;
}

function chipClass(chipColor: ChipColor): string {
  return chipColor === "gold" ? "gold" : "gray";
}

function belongsToLeftDraftStack(trackIndex: number): boolean {
  return Math.floor(trackIndex / 2) % 2 === 0;
}

function sortChips(chips: readonly NumberChip[]): readonly NumberChip[] {
  return [...chips].sort(
    (left, right) =>
      left.number - right.number || left.chipId.localeCompare(right.chipId),
  );
}

function fixedHandSlots(
  ownHandSlots: readonly KingsCrownViewerHandSlot[],
  ownHand: readonly NumberChip[],
): readonly RenderedHandSlot[] {
  const fallbackSlots =
    ownHandSlots.length > 0
      ? ownHandSlots
      : Array.from({ length: KINGS_CROWN_MAX_HAND_SIZE }, (_, slotIndex) => ({
          slotIndex,
          chip: ownHand[slotIndex] ?? null,
        }));

  return Array.from({ length: KINGS_CROWN_MAX_HAND_SIZE }, (_, slotIndex) => {
    const slot = fallbackSlots.find(
      (candidate) => candidate.slotIndex === slotIndex,
    );
    return {
      key: `fixed-${slotIndex}`,
      slotIndex,
      row: "fixed",
      chip: slot?.chip ?? null,
    };
  });
}

function sortedHandSlots(
  ownHand: readonly NumberChip[],
): readonly RenderedHandSlot[] {
  const chipsByColor: Readonly<Record<ChipColor, readonly NumberChip[]>> = {
    gray: sortChips(ownHand.filter((chip) => chip.chipColor === "gray")),
    gold: sortChips(ownHand.filter((chip) => chip.chipColor === "gold")),
  };

  return (["gray", "gold"] as const).flatMap((chipColor) =>
    Array.from({ length: KINGS_CROWN_MAX_HAND_SIZE }, (_, slotIndex) => ({
      key: `${chipColor}-${slotIndex}`,
      slotIndex,
      row: chipColor,
      chip: chipsByColor[chipColor][slotIndex] ?? null,
    })),
  );
}

function NumberChipView({
  chip,
  compact = false,
}: {
  readonly chip: NumberChip;
  readonly compact?: boolean;
}) {
  return (
    <span
      className={`kc-chip ${chipClass(chip.chipColor)} ${
        compact ? "compact" : ""
      }`}
    >
      {chip.number}
    </span>
  );
}

function DraftSlot({
  slot,
  selected,
  canPick,
  onPick,
}: {
  readonly slot: KingsCrownViewerTableSlot;
  readonly selected: boolean;
  readonly canPick: boolean;
  readonly onPick: (slotId: string) => void;
}) {
  const disabled = !canPick || slot.state !== "occupied";
  const stateClass =
    slot.state === "future"
      ? "future"
      : slot.state === "taken"
        ? "taken"
        : "occupied";
  const faceClass = slot.face === "down" ? "down" : "";
  return (
    <button
      type="button"
      className={`kc-slot ${stateClass} ${faceClass} ${selected ? "selected" : ""}`}
      data-testid={`kings-crown-table-slot-${slot.slotId}`}
      data-slot-id={slot.slotId}
      data-track-index={slot.trackIndex}
      data-state={slot.state}
      data-face={slot.face ?? "none"}
      data-has-chip={slot.chip !== null}
      disabled={disabled}
      aria-pressed={selected}
      onClick={() => onPick(slot.slotId)}
    >
      {slot.chip ? <NumberChipView chip={slot.chip} /> : null}
    </button>
  );
}

function DraftTable({
  state,
  isMyTurn,
  pickSelection,
  onPickTable,
  onPickBag,
}: {
  readonly state: KingsCrownViewerState;
  readonly isMyTurn: boolean;
  readonly pickSelection: PickSelection | null;
  readonly onPickTable: (slotId: string) => void;
  readonly onPickBag: () => void;
}) {
  const leftSlots = state.table.filter((slot) =>
    belongsToLeftDraftStack(slot.trackIndex),
  );
  const rightSlots = state.table.filter(
    (slot) => !belongsToLeftDraftStack(slot.trackIndex),
  );
  const canPick = isMyTurn && state.phase === "DRAFT_PICK";

  return (
    <section
      className="kc-card"
      data-testid="kings-crown-draft-table"
      data-phase={state.phase}
      data-bag-count={state.bagCount}
    >
      <div className="kc-draft-board">
        <div className="kc-draft-center">
          <button
            type="button"
            className={`kc-bag ${pickSelection?.source === "bag" ? "selected" : ""}`}
            data-testid="kings-crown-bag-control"
            data-bag-count={state.bagCount}
            disabled={!canPick || state.bagCount <= 0}
            aria-pressed={pickSelection?.source === "bag"}
            onClick={onPickBag}
          >
            {state.bagCount}
          </button>
        </div>
        <div className="kc-draft-side p2" data-testid="kings-crown-draft-left">
          {leftSlots.map((slot) => (
            <DraftSlot
              key={slot.slotId}
              slot={slot}
              selected={
                pickSelection?.source === "table" &&
                pickSelection.slotId === slot.slotId
              }
              canPick={canPick}
              onPick={onPickTable}
            />
          ))}
        </div>
        <div className="kc-draft-side p1" data-testid="kings-crown-draft-right">
          {rightSlots.map((slot) => (
            <DraftSlot
              key={slot.slotId}
              slot={slot}
              selected={
                pickSelection?.source === "table" &&
                pickSelection.slotId === slot.slotId
              }
              canPick={canPick}
              onPick={onPickTable}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function BoardToken({
  cell,
  ownerClass,
}: {
  readonly cell: Exclude<BoardCell, null | "CENTER">;
  readonly ownerClass: "p1" | "p2";
}) {
  return (
    <span
      className={`kc-piece ${ownerClass}`}
      data-owner-id={cell.ownerId}
      data-chip-color={cell.chip.chipColor}
      data-number={cell.chip.number}
    >
      <NumberChipView chip={cell.chip} compact />
    </span>
  );
}

function isPlacedCell(
  cell: BoardCell,
): cell is Exclude<BoardCell, null | "CENTER"> {
  return cell !== null && cell !== "CENTER";
}

function BoardGrid({
  state,
  selectedChipId,
  selectedCellId,
  onSelectCell,
}: {
  readonly state: KingsCrownViewerState;
  readonly selectedChipId: string | null;
  readonly selectedCellId: string | null;
  readonly onSelectCell: (cellId: string) => void;
}) {
  const legalTargets =
    selectedChipId === null
      ? []
      : (state.legalTargetsByChipId[selectedChipId] ?? []);
  const selectedChip =
    selectedChipId === null
      ? null
      : (state.ownHand.find((chip) => chip.chipId === selectedChipId) ?? null);
  const playerIndexById = new Map(
    state.players.map((player, index) => [player.playerId, index] as const),
  );
  return (
    <section className="kc-board" data-testid="kings-crown-board">
      {state.board.flatMap((row, rowIndex) =>
        row.map((cell, colIndex) => {
          const cellId = `r${rowIndex}c${colIndex}`;
          const placedCell = isPlacedCell(cell) ? cell : null;
          const ownerClass =
            placedCell === null
              ? null
              : (playerIndexById.get(placedCell.ownerId) ?? 0) === 0
                ? "p1"
                : "p2";
          const legal = legalTargets.includes(cellId);
          const selected = selectedCellId === cellId;
          return (
            <button
              key={cellId}
              type="button"
              className={`kc-cell ${cell === "CENTER" ? "center" : ""} ${
                ownerClass ? `owned ${ownerClass}` : ""
              } ${legal ? "legal" : ""} ${selected ? "selected" : ""}`}
              data-testid={`kings-crown-cell-${rowIndex}-${colIndex}`}
              data-cell-id={cellId}
              data-legal={legal}
              data-occupied={placedCell !== null}
              data-owner-id={placedCell?.ownerId ?? ""}
              data-owner-class={ownerClass ?? ""}
              data-chip-color={placedCell?.chip.chipColor ?? ""}
              data-number={placedCell?.chip.number ?? ""}
              disabled={!legal}
              aria-pressed={selected}
              onClick={() => onSelectCell(cellId)}
            >
              {placedCell !== null && ownerClass !== null ? (
                <BoardToken cell={placedCell} ownerClass={ownerClass} />
              ) : null}
              {placedCell === null && selected && selectedChip !== null ? (
                <span
                  className="kc-cell-preview"
                  data-testid="kings-crown-cell-preview"
                  aria-hidden="true"
                >
                  <NumberChipView chip={selectedChip} compact />
                </span>
              ) : null}
            </button>
          );
        }),
      )}
    </section>
  );
}

function HandTray({
  state,
  selectedChipId,
  sortMode,
  onSelectChip,
}: {
  readonly state: KingsCrownViewerState;
  readonly selectedChipId: string | null;
  readonly sortMode: HandSortMode;
  readonly onSelectChip: (chipId: string) => void;
}) {
  const canSelect =
    state.phase === "PLACE" && state.currentPlayerId === state.viewerPlayerId;
  const slots =
    sortMode === "fixed"
      ? fixedHandSlots(state.ownHandSlots ?? [], state.ownHand)
      : sortedHandSlots(state.ownHand);

  return (
    <div className="kc-hand-zone">
      <div
        className={`kc-hand ${sortMode}`}
        data-testid="kings-crown-hand-tray"
        data-sort-mode={sortMode}
      >
        {slots.map((slot) => {
          const chip = slot.chip;
          if (!chip) {
            return (
              <span
                key={slot.key}
                className="kc-hand-slot empty"
                data-testid="kings-crown-hand-slot-empty"
                data-hand-slot="true"
                data-slot-index={slot.slotIndex}
                data-hand-row={slot.row}
              />
            );
          }
          const legalTargets = state.legalTargetsByChipId[chip.chipId] ?? [];
          const usable = canSelect && legalTargets.length > 0;
          return (
            <button
              type="button"
              key={slot.key}
              className={`kc-hand-chip ${selectedChipId === chip.chipId ? "selected" : ""} ${
                usable ? "usable" : "dim"
              }`}
              data-testid="kings-crown-hand-chip"
              data-hand-slot="true"
              data-slot-index={slot.slotIndex}
              data-hand-row={slot.row}
              data-chip-id={chip.chipId}
              data-chip-color={chip.chipColor}
              data-number={chip.number}
              data-usable={usable}
              disabled={!usable}
              aria-pressed={selectedChipId === chip.chipId}
              onClick={() => onSelectChip(chip.chipId)}
            >
              <NumberChipView chip={chip} compact />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PlayerRail({
  state,
  getPlayerName,
}: {
  readonly state: KingsCrownViewerState;
  readonly getPlayerName: (playerId: string) => string;
}) {
  return (
    <div
      className="kc-player-rail"
      data-testid="kings-crown-phase-frame"
      data-phase={state.phase}
      data-active-player={state.currentPlayerId}
    >
      {state.players.map((player, index) => (
        <div
          key={player.playerId}
          className={`kc-seat ${player.isCurrent ? "current" : ""}`}
          data-player-id={player.playerId}
          data-current={player.isCurrent}
        >
          <span className={`marker p${index + 1}`} />
          <strong>{getPlayerName(player.playerId)}</strong>
          <span className="count">{player.remainingCount}</span>
        </div>
      ))}
    </div>
  );
}

function getActionLabel(
  state: KingsCrownViewerState,
  isMyTurn: boolean,
): string {
  if (state.phase === "ENDED") return "결과 확인";
  if (!isMyTurn) return "대기 중";
  if (state.phase === "DRAFT_DRAW") return "뽑기";
  if (state.phase === "DRAFT_PICK") return "선택";
  return "확인";
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { viewerState, lastError } = useKingsCrownMultiplayerStore();
  const actions = kingsCrownMultiplayerActions;
  const [pickSelection, setPickSelection] = useState<PickSelection | null>(
    null,
  );
  const [selectedChipId, setSelectedChipId] = useState<string | null>(null);
  const [selectedCellId, setSelectedCellId] = useState<string | null>(null);
  const [handSortMode, setHandSortMode] = useState<HandSortMode>("fixed");

  const roomId = currentRoom?.id ?? null;
  const participantIds = useMemo(
    () => currentRoom?.participantUserIds ?? [],
    [currentRoom?.participantUserIds],
  );
  const isHost = currentRoom?.hostUserId === user?.id;
  const isObserver =
    !!user?.id &&
    participantIds.length > 0 &&
    !participantIds.includes(user.id);
  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);

  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  useGameEventsWS(roomId, {
    onGameEvent: () => {},
    onViewerState: (state, seq) => {
      actionsRef.current.applyRemoteEvent(state, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: room id and participant set are the init boundary; room_updated handles refreshed metadata.
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;
    if (participantIds.length !== 2 && !isObserver) return;

    if (isHost) {
      void actions.initializeAsHost(
        roomId,
        participantIds,
        currentRoom.hostUserId,
      );
    } else {
      void actions.initializeAsViewer(roomId, isObserver);
    }

    return () => {
      actions.reset();
    };
  }, [
    roomId,
    currentRoom?.id,
    user?.id,
    isHost,
    isObserver,
    participantIds.join("|"),
  ]);

  const phaseKey = `${viewerState?.phase ?? "none"}:${viewerState?.currentPlayerId ?? "none"}:${viewerState?.ownHand.length ?? 0}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: this key intentionally resets local selection when server-projected turn boundaries change.
  useEffect(() => {
    setPickSelection(null);
    setSelectedChipId(null);
    setSelectedCellId(null);
  }, [phaseKey]);

  const getPlayerName = (playerId: string): string =>
    playerId === viewerState?.viewerPlayerId
      ? "나"
      : getParticipantDisplayName(currentRoom, playerId, "상대");

  function leaveGame(): void {
    actions.reset();
    void lobbyActions.leaveGame();
    navigateTo("/kings-crown/lobby");
  }

  async function handleAction(): Promise<void> {
    if (!viewerState) return;
    if (viewerState.phase === "ENDED") return;
    const isMyTurn = viewerState.currentPlayerId === viewerState.viewerPlayerId;
    if (!isMyTurn) return;

    if (viewerState.phase === "DRAFT_DRAW") {
      await actions.drawDraftOptions();
      return;
    }
    if (viewerState.phase === "DRAFT_PICK" && pickSelection) {
      await actions.pickDraftToken(pickSelection.source, pickSelection.slotId);
      return;
    }
    if (viewerState.phase === "PLACE" && selectedChipId && selectedCellId) {
      await actions.confirmPlacement(selectedChipId, selectedCellId);
    }
  }

  function clearSelections(): void {
    setPickSelection(null);
    setSelectedChipId(null);
    setSelectedCellId(null);
  }

  if (!currentRoom) {
    return (
      <div data-testid="game-screen">
        <div className="kc-screen" data-testid="kings-crown-screen">
          <section className="kc-empty-card">
            <p>게임 방이 없습니다.</p>
            <button type="button" className="kc-button" onClick={leaveGame}>
              로비로 이동
            </button>
          </section>
        </div>
      </div>
    );
  }

  if (!viewerState) {
    return (
      <div data-testid="game-screen">
        <div className="kc-screen" data-testid="kings-crown-screen">
          <section className="kc-empty-card">
            <p>게임을 준비하고 있습니다.</p>
          </section>
        </div>
      </div>
    );
  }

  const isMyTurn = viewerState.currentPlayerId === viewerState.viewerPlayerId;
  const actionLabel = getActionLabel(viewerState, isMyTurn);
  const actionDisabled =
    viewerState.phase === "ENDED" ||
    !isMyTurn ||
    (viewerState.phase === "DRAFT_PICK" && pickSelection === null) ||
    (viewerState.phase === "PLACE" && (!selectedChipId || !selectedCellId));
  const hasSelection =
    pickSelection !== null ||
    selectedChipId !== null ||
    selectedCellId !== null;
  const resultTitle = viewerState.winnerId
    ? `${getPlayerName(viewerState.winnerId)} 승리`
    : "게임 종료";
  const resultReason =
    viewerState.winLine.length > 0 ? "빙고 완성" : "배치 불가";

  return (
    <div data-testid="game-screen">
      <div
        className="kc-screen"
        data-testid="kings-crown-screen"
        data-phase={viewerState.phase}
        data-current-player={viewerState.currentPlayerId}
        data-viewer={viewerState.viewerPlayerId}
      >
        <div className="kc-shell">
          <PlayerRail state={viewerState} getPlayerName={getPlayerName} />

          {viewerState.phase === "DRAFT_DRAW" ||
          viewerState.phase === "DRAFT_PICK" ? (
            <DraftTable
              state={viewerState}
              isMyTurn={isMyTurn}
              pickSelection={pickSelection}
              onPickBag={() => setPickSelection({ source: "bag" })}
              onPickTable={(slotId) =>
                setPickSelection({ source: "table", slotId })
              }
            />
          ) : (
            <BoardGrid
              state={viewerState}
              selectedChipId={selectedChipId}
              selectedCellId={selectedCellId}
              onSelectCell={setSelectedCellId}
            />
          )}

          {viewerState.phase === "ENDED" && (
            <div
              className="kc-result-backdrop"
              data-testid="kings-crown-result-panel"
              role="dialog"
              aria-modal="true"
              aria-label="게임 결과"
            >
              <section className="kc-result">
                <strong>{resultTitle}</strong>
                <span>{resultReason}</span>
                <button
                  type="button"
                  className="kc-button secondary"
                  data-testid="kings-crown-result-lobby"
                  onClick={leaveGame}
                >
                  로비로
                </button>
              </section>
            </div>
          )}

          <section className="kc-control-panel">
            <HandTray
              state={viewerState}
              selectedChipId={selectedChipId}
              sortMode={handSortMode}
              onSelectChip={(chipId) => {
                setSelectedChipId(chipId);
                setSelectedCellId(null);
              }}
            />
            <div
              className="kc-action-dock"
              data-testid="kings-crown-action-dock"
            >
              <button
                type="button"
                className={`kc-hand-toggle ${
                  handSortMode === "sorted" ? "active" : ""
                }`}
                data-testid="kings-crown-hand-sort-toggle"
                aria-label="손패 자동 정렬"
                aria-pressed={handSortMode === "sorted"}
                onClick={() =>
                  setHandSortMode((mode) =>
                    mode === "fixed" ? "sorted" : "fixed",
                  )
                }
              >
                <span className="kc-tray-icon" aria-hidden="true" />
              </button>
              {lastError && <span className="kc-error">{lastError.error}</span>}
              <span className="grow" />
              <button
                type="button"
                className="kc-button secondary"
                data-testid="kings-crown-clear-selection"
                disabled={!hasSelection}
                aria-label="선택 초기화"
                onClick={clearSelections}
              >
                초기화
              </button>
              <button
                type="button"
                className="kc-button primary"
                data-testid="kings-crown-primary-action"
                data-action={actionLabel}
                disabled={actionDisabled}
                onClick={() => void handleAction()}
              >
                {actionLabel}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
